/* eslint-disable no-inner-declarations */
import { backendService } from './backendService.js';
import { specStore, editorStateStore, claimsStore } from '../stores/editorsStore.js';
import { settingsStore } from '../stores/settingsStore.js';
import { idfTextStore } from '../stores/idfTextStore.js';
import { get } from 'svelte/store';
import { DEFAULT_BOILERPLATES } from '../stores/boilerplatesStore.js';

// Regex pattern for matching @{figure:...} and @{numeral:...} references in boilerplate text
const BOILERPLATE_REFERENCE_PATTERN = /@\{(figure|numeral):([^}]+)\}/g;

/**
 * Parse boilerplate text for figure/numeral references and create text nodes with marks
 * @param {string} text - Text to parse
 * @param {Object} schema - ProseMirror schema
 * @returns {Array} Array of text nodes with appropriate marks
 */
function parseBoilerplateReferences(text, schema) {
    const textNodes = [];
    let lastIndex = 0;
    let match;

    // Reset regex lastIndex
    BOILERPLATE_REFERENCE_PATTERN.lastIndex = 0;

    while ((match = BOILERPLATE_REFERENCE_PATTERN.exec(text)) !== null) {
        // Add text before the match
        if (match.index > lastIndex) {
            const beforeText = text.slice(lastIndex, match.index);
            if (beforeText) {
                textNodes.push(schema.text(beforeText));
            }
        }

        // Figure or numeral reference (@{figure:...} or @{numeral:...})
        const fullMatch = match[0];     // e.g., "@{figure:ar_device}"
        const refType = match[1];       // "figure" or "numeral"
        const refContent = match[2];    // e.g., "ar_device" or "head_102"

        // Create a placeholder figureKey - works even if figure doesn't exist
        const figureKey = `generated:${refType}:${refContent}`;

        const figureRefMark = schema.marks.figureReference;
        if (figureRefMark) {
            textNodes.push(schema.text(fullMatch, [figureRefMark.create({ figureKey })]));
        } else {
            // Fallback if figureReference mark doesn't exist
            textNodes.push(schema.text(fullMatch));
        }

        lastIndex = BOILERPLATE_REFERENCE_PATTERN.lastIndex;
    }

    // Add remaining text after last match
    if (lastIndex < text.length) {
        const afterText = text.slice(lastIndex);
        if (afterText) {
            textNodes.push(schema.text(afterText));
        }
    }

    // If no matches found, return the original text
    if (textNodes.length === 0 && text) {
        textNodes.push(schema.text(text));
    }

    return textNodes;
}

// Helper function to find existing regenerative blocks
function findExistingRegenerativeBlocks(content) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const blocks = doc.querySelectorAll('p[data-block-type="regenerative-support"]');
    const contexts = new Set();
    blocks.forEach(block => {
        const context = block.getAttribute('data-context');
        if (context) contexts.add(context);
    });
    return contexts;
}

/**
 * Validate claims from the frontend claimsStore
 * @returns {Object} - { isValid: boolean, error: string | null }
 */
function validateClaims() {
    const claimsData = get(claimsStore);
    const claimsText = claimsData.content || '';

    if (!claimsText.trim()) {
        return { isValid: false, error: '[[Error: please add claims text]]' };
    }
    
    // Find claim 1 and check if it has content and ends with period
    const lines = claimsText.split('\n');
    let claim1Content = '';
    let foundClaim1 = false;
    
    for (const line of lines) {
        const stripped = line.trim();

        // Skip comment lines and empty lines
        if (stripped.startsWith('#') || stripped === '') {
            continue;
        }

        if (stripped.startsWith('1.')) {
            foundClaim1 = true;
            claim1Content = stripped.substring(2).trim();
            // Continue reading subsequent lines for multiline claims
        } else if (foundClaim1 && !stripped.match(/^\d+\./)) {
            // This is a continuation of claim 1 (any line that doesn't start with a claim number)
            claim1Content += ' ' + stripped;
        } else if (foundClaim1 && stripped.match(/^\d+\./)) {
            // Hit another claim number, stop parsing claim 1
            break;
        }
    }
    
    if (!foundClaim1 || !claim1Content || !claim1Content.endsWith('.')) {
        return { isValid: false, error: '[[Error: please add claims text]]' };
    }

    return { isValid: true, error: null };
}

/**
 * Extract a user-friendly error type from an error message
 * @param {string} message - The error message from the backend
 * @returns {string} - A user-friendly error type description
 */
function extractErrorType(message) {
    if (!message) return 'generation failure';

    const lowerMessage = message.toLowerCase();

    if (message.includes('[TEST]')) {
        return 'test error';
    } else if (lowerMessage.includes('timeout')) {
        return 'timeout';
    } else if (lowerMessage.includes('network') || lowerMessage.includes('connection')) {
        return 'network error';
    } else if (lowerMessage.includes('model failed') || lowerMessage.includes('model error')) {
        return 'model error';
    } else if (lowerMessage.includes('rate limit')) {
        return 'rate limit';
    } else if (lowerMessage.includes('authentication') || lowerMessage.includes('unauthorized')) {
        return 'authentication error';
    } else if (lowerMessage.includes('validation')) {
        return 'validation error';
    }

    return 'generation failure';
}

/**
 * Insert error message into regenerative blocks
 * @param {Object} specEditor - The spec editor instance
 * @param {string} context - The context to update
 * @param {string} errorMessage - The error message to display
 */
function insertErrorIntoRegenerativeBlock(specEditor, context, errorMessage) {
    const doc = specEditor.state.doc;
    let tr = specEditor.state.tr;

    // Collect all blocks to replace first (to handle position shifts correctly)
    const blocksToReplace = [];

    // Find regenerative blocks OR skeleton-loader blocks for this context
    // (skeleton-loader blocks are created by init events before content/error arrives)
    doc.descendants((node, pos) => {
        if (node.type.name === 'paragraph' &&
            (node.attrs.blockType === 'regenerative-support' || node.attrs.blockType === 'skeleton-loader') &&
            node.attrs.context === context) {
            blocksToReplace.push({ pos, size: node.nodeSize });
        }
    });

    console.log(`[ERROR-INSERT] Found ${blocksToReplace.length} blocks to replace for context ${context}`);

    if (blocksToReplace.length === 0) {
        console.log(`[ERROR-INSERT] No blocks found for context ${context}, skipping`);
        return;
    }

    // Replace all blocks with a single error node (delete all, insert one at first position)
    // Process in reverse order to maintain positions
    const firstBlockPos = blocksToReplace[0].pos;

    // Delete all blocks in reverse order
    for (let i = blocksToReplace.length - 1; i >= 0; i--) {
        const block = blocksToReplace[i];
        tr = tr.delete(block.pos, block.pos + block.size);
    }

    // Insert a single error node at the first block's position
    const errorNode = specEditor.schema.nodes.paragraph.create(
        { blockType: 'regenerative-support', context: context },
        specEditor.schema.text(errorMessage)
    );
    tr = tr.insert(firstBlockPos, errorNode);

    if (tr.docChanged) {
        specEditor.view.dispatch(tr);
        console.log(`[ERROR-INSERT] Replaced ${blocksToReplace.length} blocks with error for context ${context}`);
    }
}

/**
 * Insert multiple sub-component error messages as individual paragraphs
 * Used for detailed-description to show each sub-component error separately
 * @param {Object} specEditor - The spec editor instance
 * @param {string} context - The context to update (e.g., 'detailed-description')
 * @param {Array} errors - Array of {subcomponent, name, message} objects
 */
function insertMultipleSubcomponentErrors(specEditor, context, errors) {
    const doc = specEditor.state.doc;
    let tr = specEditor.state.tr;

    // Collect all blocks to replace first (to handle position shifts correctly)
    const blocksToReplace = [];

    // Find regenerative blocks OR skeleton-loader blocks for this context
    doc.descendants((node, pos) => {
        if (node.type.name === 'paragraph' &&
            (node.attrs.blockType === 'regenerative-support' || node.attrs.blockType === 'skeleton-loader') &&
            node.attrs.context === context) {
            blocksToReplace.push({ pos, size: node.nodeSize });
        }
    });

    console.log(`[ERROR-INSERT-MULTI] Found ${blocksToReplace.length} blocks to replace for context ${context}`);
    console.log(`[ERROR-INSERT-MULTI] Will insert ${errors.length} sub-component error paragraphs`);

    if (blocksToReplace.length === 0) {
        console.log(`[ERROR-INSERT-MULTI] No blocks found for context ${context}, skipping`);
        return;
    }

    // Get the first block position for insertion
    const firstBlockPos = blocksToReplace[0].pos;

    // Delete all blocks in reverse order to maintain positions
    for (let i = blocksToReplace.length - 1; i >= 0; i--) {
        const block = blocksToReplace[i];
        tr = tr.delete(block.pos, block.pos + block.size);
    }

    // Insert a paragraph for each sub-component error
    let currentPos = firstBlockPos;
    for (const error of errors) {
        // Extract error type from the message (e.g., "[TEST] Forced error for X" -> "test error")
        let errorType = 'generation failure';
        if (error.message) {
            if (error.message.includes('[TEST]')) {
                errorType = 'test error';
            } else if (error.message.toLowerCase().includes('timeout')) {
                errorType = 'timeout';
            } else if (error.message.toLowerCase().includes('network') || error.message.toLowerCase().includes('connection')) {
                errorType = 'network error';
            } else if (error.message.toLowerCase().includes('model')) {
                errorType = 'model error';
            }
        }
        const errorMessage = `[[Error: unable to generate ${error.name} due to ${errorType}. Please try again.]]`;
        const errorNode = specEditor.schema.nodes.paragraph.create(
            { blockType: 'regenerative-support', context: context },
            specEditor.schema.text(errorMessage)
        );
        tr = tr.insert(currentPos, errorNode);
        currentPos += errorNode.nodeSize;
    }

    if (tr.docChanged) {
        specEditor.view.dispatch(tr);
        console.log(`[ERROR-INSERT-MULTI] Inserted ${errors.length} error paragraphs for ${context}`);
    }
}

/**
 * Get a boilerplate by its ID from DEFAULT_BOILERPLATES
 * @param {string} boilerplateId - The boilerplate ID (e.g., 'default-incorporation-by-reference')
 * @returns {Object|null} - The boilerplate object or null if not found
 */
function getBoilerplateById(boilerplateId) {
    return DEFAULT_BOILERPLATES.find(b => b.id === boilerplateId) || null;
}

/**
 * Check if boilerplate content already exists in the document
 * Uses a key phrase from the boilerplate to detect existence
 * @param {Object} specEditor - The spec editor instance
 * @param {string} boilerplateId - The boilerplate ID to check for
 * @returns {boolean} - True if boilerplate content already exists
 */
function boilerplateExistsInDocument(specEditor, boilerplateId) {
    const boilerplate = getBoilerplateById(boilerplateId);
    if (!boilerplate) return false;

    // Get first 50 characters as a key phrase to check for existence
    const keyPhrase = boilerplate.content.substring(0, 50).toLowerCase();
    const docContent = specEditor.state.doc.textContent.toLowerCase();

    return docContent.includes(keyPhrase);
}

/**
 * Find the position at the end of a section (before next section header)
 * @param {Object} specEditor - The spec editor instance
 * @param {string} sectionType - The section type to find end of
 * @returns {number|null} - The position at end of section or null if not found
 */
function findEndOfSection(specEditor, sectionType) {
    const doc = specEditor.state.doc;
    let sectionStart = null;
    let sectionEnd = null;

    doc.descendants((node, pos) => {
        if (node.type.name === 'heading' && node.attrs.sectionType) {
            if (node.attrs.sectionType === sectionType) {
                sectionStart = pos;
            } else if (sectionStart !== null && sectionEnd === null) {
                // Found the next section header, section ends here
                sectionEnd = pos;
                return false;
            }
        }
    });

    // If section found but no next section, end is at document end
    if (sectionStart !== null && sectionEnd === null) {
        sectionEnd = doc.content.size;
    }

    return sectionEnd;
}

/**
 * Find the position right after a section header
 * @param {Object} specEditor - The spec editor instance
 * @param {string} sectionType - The section type to find
 * @returns {number|null} - The position after the header or null if not found
 */
function findPositionAfterSectionHeader(specEditor, sectionType) {
    const doc = specEditor.state.doc;
    let insertPos = null;

    doc.descendants((node, pos) => {
        if (node.type.name === 'heading' && node.attrs.sectionType === sectionType) {
            insertPos = pos + node.nodeSize;
            return false;
        }
    });

    return insertPos;
}

/**
 * Check if a section exists in the document
 * @param {Object} specEditor - The spec editor instance
 * @param {string} sectionType - The section type to check for
 * @returns {boolean} - True if section exists
 */
function sectionExists(specEditor, sectionType) {
    const doc = specEditor.state.doc;
    let exists = false;

    doc.descendants((node) => {
        if (node.type.name === 'heading' && node.attrs.sectionType === sectionType) {
            exists = true;
            return false;
        }
    });

    return exists;
}

/**
 * Insert boilerplate content at a specific position
 * @param {Object} specEditor - The spec editor instance
 * @param {string} boilerplateId - The boilerplate ID to insert
 * @param {number} insertPos - The position to insert at
 * @param {Object} options - Additional options (e.g., addHeading, headingText)
 */
function insertBoilerplateAtPosition(specEditor, boilerplateId, insertPos, options = {}) {
    const boilerplate = getBoilerplateById(boilerplateId);
    if (!boilerplate || insertPos === null) return;

    let tr = specEditor.state.tr;
    let currentPos = insertPos;

    // Optionally add a heading before the boilerplate content
    if (options.addHeading && options.headingText) {
        const headingNode = specEditor.schema.nodes.heading.create(
            { level: 3, class: 'subsection' },
            specEditor.schema.text(options.headingText)
        );
        tr = tr.insert(currentPos, headingNode);
        currentPos += headingNode.nodeSize;
    }

    // Split boilerplate content into separate paragraphs (on double newlines or single newlines)
    const paragraphs = boilerplate.content
        .split(/\n\n+/)  // Split on double newlines (paragraph breaks)
        .map(p => p.trim())
        .filter(p => p.length > 0);

    // Insert each paragraph as a separate node, parsing figure references
    for (const paragraphText of paragraphs) {
        // Parse the text for figure/numeral references and create proper marks
        const textNodes = parseBoilerplateReferences(paragraphText, specEditor.schema);
        const paragraphNode = specEditor.schema.nodes.paragraph.create(
            {},
            textNodes
        );
        tr = tr.insert(currentPos, paragraphNode);
        currentPos += paragraphNode.nodeSize;
    }

    if (tr.docChanged) {
        specEditor.view.dispatch(tr);
    }
}

/**
 * Insert boilerplates that should come after a specific context completes
 * This ensures boilerplates are inserted in document order during regeneration
 *
 * Document order: title → field → background → summary → incorporation → drawings → detailed → embodiments → examples → abstract
 *
 * @param {Object} specEditor - The spec editor instance
 * @param {string} completedContext - The context that just completed
 * @param {Set} insertedBoilerplates - Track which boilerplates have been inserted
 */
function insertBoilerplatesAfterContext(specEditor, completedContext, insertedBoilerplates) {
    console.log(`[BOILERPLATE] Checking boilerplates after context: ${completedContext}`);

    // After 'summary' completes → insert Incorporation by Reference (for incorporation section)
    // and Drawings Intro + Software Figures (for drawings section)
    // because these sections come after summary in document order
    if (completedContext === 'summary') {
        // 1. Incorporation by Reference
        if (!insertedBoilerplates.has('default-incorporation-by-reference') &&
            sectionExists(specEditor, 'incorporation') &&
            !boilerplateExistsInDocument(specEditor, 'default-incorporation-by-reference')) {
            console.log('[BOILERPLATE] Inserting Incorporation by Reference after summary');
            const pos = findPositionAfterSectionHeader(specEditor, 'incorporation');
            insertBoilerplateAtPosition(specEditor, 'default-incorporation-by-reference', pos);
            insertedBoilerplates.add('default-incorporation-by-reference');
        }

        // 2. Drawings Intro
        if (!insertedBoilerplates.has('default-drawings-intro') &&
            sectionExists(specEditor, 'drawings') &&
            !boilerplateExistsInDocument(specEditor, 'default-drawings-intro')) {
            console.log('[BOILERPLATE] Inserting Drawings Intro after summary');
            const pos = findPositionAfterSectionHeader(specEditor, 'drawings');
            insertBoilerplateAtPosition(specEditor, 'default-drawings-intro', pos);
            insertedBoilerplates.add('default-drawings-intro');
        }
    }

    // After 'detailed-description' completes → insert Definitions at the end of detailed section
    if (completedContext === 'detailed-description') {
        if (!insertedBoilerplates.has('default-definitions') &&
            sectionExists(specEditor, 'detailed') &&
            !boilerplateExistsInDocument(specEditor, 'default-definitions')) {
            console.log('[BOILERPLATE] Inserting Definitions after detailed-description');
            const pos = findEndOfSection(specEditor, 'detailed');
            insertBoilerplateAtPosition(specEditor, 'default-definitions', pos, {
                addHeading: true,
                headingText: 'Definitions'
            });
            insertedBoilerplates.add('default-definitions');
        }
    }

    // After 'embodiments' completes → insert Specification Boilerplate
    // Goes at end of examples if that section exists, otherwise at end of embodiments
    if (completedContext === 'embodiments') {
        const specBoilerplateId = 'default-specification-boilerplate';
        if (!insertedBoilerplates.has(specBoilerplateId) &&
            !boilerplateExistsInDocument(specEditor, specBoilerplateId)) {

            if (sectionExists(specEditor, 'examples')) {
                console.log('[BOILERPLATE] Inserting Specification Boilerplate at end of Examples');
                const pos = findEndOfSection(specEditor, 'examples');
                insertBoilerplateAtPosition(specEditor, specBoilerplateId, pos);
            } else if (sectionExists(specEditor, 'embodiments')) {
                console.log('[BOILERPLATE] Inserting Specification Boilerplate at end of Embodiments');
                const pos = findEndOfSection(specEditor, 'embodiments');
                insertBoilerplateAtPosition(specEditor, specBoilerplateId, pos);
            }
            insertedBoilerplates.add(specBoilerplateId);
        }
    }
}

/**
 * Regenerates the document content based on claims
 * @param {Object} specEditor - The spec editor instance
 * @param {string} claims - The claims content
 * @param {Array} contexts - The contexts to regenerate
 * @param {Object} options - Options including progress callback
 * @returns {Promise<boolean>} - True if regeneration was successful
 */
export async function regenerateDocument(specEditor, claims, contexts = [], options = {}) {
    if (!specEditor) {
        console.error('DocumentRegenerationService: No spec editor reference');
        return false;
    }

    console.time('Total regeneration time');
    console.log('DocumentRegenerationService: Starting spec regeneration');
    editorStateStore.update(s => ({ ...s, isAnalyzing: true }));
    
    try {
        // Set isRegenerating to true (this will trigger the spinner animation)
        console.log('DocumentRegenerationService: Setting isRegenerating to true');
        specStore.update(s => ({ ...s, isRegenerating: true }));
        
        // Get current content from editor
        console.time('Content preparation');
        const currentContent = specEditor.getHTML();
        console.log('DocumentRegenerationService: Current editor content:', currentContent);
        
        console.log('DocumentRegenerationService: Current claims:', claims);
        
        // Find which regenerative blocks exist
        const existingContexts = findExistingRegenerativeBlocks(currentContent);
        console.log('DocumentRegenerationService: Found existing contexts:', existingContexts);
        console.log('DocumentRegenerationService: Selected contexts from dropdown:', contexts);

        // Debug: Check if field regenerative block exists in HTML
        console.log('[DEBUG-FIELD] Checking for field block in HTML...');
        const hasFieldBlock = currentContent.includes('data-context="field"');
        console.log('[DEBUG-FIELD] HTML contains data-context="field":', hasFieldBlock);
        if (hasFieldBlock) {
            // Extract the field block to see what it looks like
            const parser = new DOMParser();
            const doc = parser.parseFromString(currentContent, 'text/html');
            const fieldBlock = doc.querySelector('p[data-context="field"]');
            console.log('[DEBUG-FIELD] Field block found:', fieldBlock);
            console.log('[DEBUG-FIELD] Field block attributes:', {
                blockType: fieldBlock?.getAttribute('data-block-type'),
                context: fieldBlock?.getAttribute('data-context'),
                class: fieldBlock?.getAttribute('class'),
                textContent: fieldBlock?.textContent
            });
        }

        // Check if title heading exists (title is a heading, not a regenerative block)
        const hasTitleHeading = currentContent.includes('data-section-type="title"');
        if (hasTitleHeading) {
            existingContexts.add('title');
            console.log('DocumentRegenerationService: Found title heading, added to existing contexts');
        }

        // Check if abstract section heading exists (abstract content goes under the abstract heading)
        const hasAbstractHeading = currentContent.includes('data-section-type="abstract"');
        if (hasAbstractHeading) {
            existingContexts.add('abstract');
            console.log('DocumentRegenerationService: Found abstract heading, added to existing contexts');
        }

        // Use selected contexts if provided, otherwise fall back to existing contexts
        const selectedContexts = contexts.length > 0 ? contexts : Array.from(existingContexts);

        console.log('[DEBUG-FIELD] ========== CONTEXT FILTERING DEBUG ==========');
        console.log('[DEBUG-FIELD] contexts parameter passed in:', contexts);
        console.log('[DEBUG-FIELD] contexts.length:', contexts.length);
        console.log('[DEBUG-FIELD] existingContexts (from findExistingRegenerativeBlocks):', Array.from(existingContexts));
        console.log('[DEBUG-FIELD] selectedContexts (after logic):', selectedContexts);
        console.log('[DEBUG-FIELD] Does existingContexts have "field"?', existingContexts.has('field'));
        console.log('[DEBUG-FIELD] Does selectedContexts include "field"?', selectedContexts.includes('field'));

        // Pre-validate claims for background and overview contexts only
        const validation = validateClaims();
        let contextsToRegenerate = selectedContexts.filter(context => existingContexts.has(context));

        console.log('[DEBUG-FIELD] contextsToRegenerate AFTER filtering:', contextsToRegenerate);
        console.log('[DEBUG-FIELD] Was "field" filtered out?', contexts.includes('field') && !contextsToRegenerate.includes('field'));

        // If validation fails, insert errors into background and overview blocks but continue with other regeneration
        if (!validation.isValid) {
            console.log('DocumentRegenerationService: Claims validation failed for background/overview:', validation.error);
            const errorContexts = ['background', 'overview'];
            for (const context of existingContexts) {
                if (errorContexts.includes(context)) {
                    insertErrorIntoRegenerativeBlock(specEditor, context, validation.error);
                }
            }
            // Remove background and overview from contexts to regenerate
            contextsToRegenerate = contextsToRegenerate.filter(context => !errorContexts.includes(context));
            if (contextsToRegenerate.length === 0) {
                return true; // No valid contexts to regenerate
            }
        }

        console.log('[DEBUG-FIELD] FINAL contextsToRegenerate:', contextsToRegenerate);
        console.log('[DEBUG-FIELD] ========== END CONTEXT FILTERING DEBUG ==========');
        console.log('DocumentRegenerationService: Contexts to regenerate:', contextsToRegenerate);
        console.timeEnd('Content preparation');
        
        // Create a temporary container for the existing content
        // Use a document fragment to ensure DOM operations work correctly
        const tempDoc = document.implementation.createHTMLDocument();
        const tempDiv = tempDoc.body;
        tempDiv.innerHTML = currentContent;
        
        console.log('[DEBUG-REGEN-EMPTY] Initial content check:');
        const initialRegBlocks = tempDiv.querySelectorAll('p[data-block-type="regenerative-support"]');
        console.log('[DEBUG-REGEN-EMPTY] Found', initialRegBlocks.length, 'existing regenerative blocks');
        
        // Log details about existing blocks
        initialRegBlocks.forEach((block, idx) => {
            const blockText = block.textContent || '';
            const isEmpty = blockText.trim().length === 0;
            console.log(`[DEBUG-REGEN-EMPTY] Existing block ${idx}: context="${block.getAttribute('data-context')}", content="${blockText.substring(0, 50)}...", empty=${isEmpty}`);
        });
        
        // Track progress state
        let hasUpdates = false;
        const processedClaims = new Map(); // Changed to Map to store key-value pairs
        
        // Store literal support generated during brief summary for reuse in detailed description
        let summaryLiteralSupport = null;
        
        // Time estimation: seconds per model call (overestimate for better UX)
        const SECONDS_PER_MODEL_CALL = 10;

        // Count claims upfront for time estimation
        const claimLines = claims.split('\n').filter(line => {
            const trimmed = line.trim();
            return trimmed && !trimmed.startsWith('#') && /^\d+\./.test(trimmed);
        });
        const claimCount = claimLines.length || 1; // At least 1 claim

        // Progress tracking for the UI
        const progressState = {
            totalSteps: 0,
            completedSteps: 0,
            currentContext: '',
            totalGroups: {},
            completedGroups: {},
            startTime: Date.now(),
            // Track completed contexts for time estimation
            completedContexts: new Set(),
            // Expected time per context (calculated upfront)
            contextExpectedSeconds: {}
        };

        // Calculate expected time per context upfront
        for (const context of contextsToRegenerate) {
            if (context === 'summary') {
                // Summary: 1 model call per claim, track remaining claims for incremental progress
                progressState.totalGroups.summary = 0;
                progressState.summaryRemainingClaims = claimCount;
            } else if (context === 'overview') {
                progressState.totalGroups[context] = 1;
                progressState.totalSteps += 1;
                progressState.contextExpectedSeconds.overview = SECONDS_PER_MODEL_CALL;
            } else if (context === 'embodiments') {
                progressState.totalGroups[context] = 1;
                progressState.totalSteps += 1;
                progressState.contextExpectedSeconds.embodiments = (claimCount + 1) * SECONDS_PER_MODEL_CALL;
            } else if (context === 'detailed-description') {
                progressState.totalGroups[context] = 1;
                progressState.totalSteps += 1;
                progressState.contextExpectedSeconds['detailed-description'] = claimCount * SECONDS_PER_MODEL_CALL;
            } else {
                // Single model call sections: title, field, background, abstract
                progressState.totalGroups[context] = 1;
                progressState.totalSteps += 1;
                progressState.contextExpectedSeconds[context] = SECONDS_PER_MODEL_CALL;
            }
        }

        // Helper: recalculate remaining time by summing incomplete contexts
        const getEstimatedRemainingTime = () => {
            let remaining = 0;
            for (const context of contextsToRegenerate) {
                if (!progressState.completedContexts.has(context)) {
                    if (context === 'summary') {
                        // Summary time based on remaining claims (decrements as groups complete)
                        remaining += (progressState.summaryRemainingClaims || 0) * SECONDS_PER_MODEL_CALL;
                    } else {
                        remaining += progressState.contextExpectedSeconds[context] || 0;
                    }
                }
            }
            return remaining;
        };

        // Helper: mark a context as complete
        const markContextComplete = (context) => {
            progressState.completedContexts.add(context);
        };
        
        // Report initial progress with total expected time
        if (options.onProgress) {
            options.onProgress({
                type: 'start',
                totalSteps: progressState.totalSteps,
                contexts: contextsToRegenerate,
                estimatedTime: getEstimatedRemainingTime()
            });
        }
        
        // Process updates immediately - no queuing needed
        
        // Don't clear existing blocks at the start - keep them visible during streaming
        // They will be cleared when new content arrives for each specific context
        console.log('DocumentRegenerationService: Keeping existing blocks visible during generation for contexts:', existingContexts);

        // Track which boilerplates have been inserted during this regeneration
        const insertedBoilerplates = new Set();

        // Call backend service with streaming support and progress callback
        console.time('Backend service call');
        console.log('[DEBUG-REGEN-SERVICE] Contexts being sent to backend:', contextsToRegenerate);
        console.log('[DEBUG-REGEN-SERVICE] Has title?', contextsToRegenerate.includes('title'));

        // Get IDF text from idfTextStore for detailed description generation
        const idfText = get(idfTextStore) || '';
        console.log('[DEBUG-REGEN-SERVICE] IDF text available:', idfText ? `${idfText.length} chars` : 'none');

        // Extract current title from editor for abstract generation
        let existingTitle = '';
        if (contextsToRegenerate.includes('abstract')) {
            const doc = specEditor.state.doc;
            doc.descendants((node) => {
                if (node.type.name === 'heading' &&
                    node.attrs.level === 2 &&
                    node.attrs.sectionType === 'title') {
                    existingTitle = node.textContent || '';
                    return false; // Stop searching
                }
            });
            console.log('[DEBUG-REGEN-SERVICE] Existing title for abstract:', existingTitle || 'none');
        }

        const result = await backendService.regenerateSpecStream(
            claims,
            currentContent,
            contextsToRegenerate,
            {
                // IDF text for problem introduction generation
                inventionDisclosure: idfText,
                // Title for abstract generation
                title: existingTitle,
                // Progress callback to handle incremental updates
                onProgress: (progressData) => {
                    console.log('[DEBUG-REGEN-STREAM] ========= PROGRESS UPDATE =========');
                    console.log('[DEBUG-REGEN-STREAM] Received progress update:', {
                        type: progressData.type,
                        context: progressData.context,
                        claim_numbers: progressData.claim_numbers,
                        contentLength: progressData.content ? progressData.content.length : 0
                    });

                    const context = progressData.context;
                    
                    // Update progress state based on event type
                    if (progressData.type === 'init') {
                        progressState.currentContext = context;

                        // Update step counts for summary
                        if (context === 'summary' && progressData.total_groups) {
                            progressState.totalGroups.summary = progressData.total_groups;
                            progressState.totalSteps += progressData.total_groups;
                            progressState.completedGroups.summary = 0;
                        }

                        // Report progress update with time-based estimate
                        if (options.onProgress) {
                            options.onProgress({
                                type: 'progress',
                                currentContext: context,
                                totalSteps: progressState.totalSteps,
                                completedSteps: progressState.completedSteps,
                                progress: (progressState.completedSteps / progressState.totalSteps) * 100,
                                estimatedTime: getEstimatedRemainingTime()
                            });
                        }
                    }

                    // Handle validation errors
                    if (progressData.type === 'validation_error') {
                        console.error('[SPEC-REGEN] Validation errors in progress callback:', progressData.validation_errors);
                        
                        // Insert error messages into all regenerative blocks
                        const errorContexts = ['background', 'overview', 'summary', 'embodiments', 'detailed-description'];
                        for (const errorContext of errorContexts) {
                            if (existingContexts.has(errorContext)) {
                                const errorMessage = '[[Error: Claims validation failed. Please fix claims structure first.]]';
                                insertErrorIntoRegenerativeBlock(specEditor, errorContext, errorMessage);
                            }
                        }
                        
                        // Stop processing and return early
                        return false;
                    }

                    // Handle init messages to create skeleton loaders
                    if (progressData.type === 'init') {
                        console.log('[DEBUG-SKELETON] Init event received:', progressData);
                        if (context === 'field') {
                            console.log(`[DEBUG-SKELETON] Creating 1 skeleton block for field`);
                            // Insert single skeleton block for field
                            insertSkeletonBlocks(context, 'field', 1);
                        } else if (context === 'summary' && progressData.total_groups) {
                            console.log(`[DEBUG-SKELETON] Creating ${progressData.total_groups} skeleton blocks for summary`);
                            // Insert skeleton blocks for summary groups
                            insertSkeletonBlocks(context, 'summary', progressData.total_groups);
                        } else if (context === 'embodiments' && progressData.total_claims) {
                            console.log(`[DEBUG-SKELETON] Creating ${progressData.total_claims + 1} skeleton blocks for embodiments`);
                            // Insert skeleton blocks for embodiments (total_claims + 1)
                            insertSkeletonBlocks(context, 'embodiments', progressData.total_claims + 1);
                        } else if (context === 'background' && progressData.total_claims) {
                            console.log(`[DEBUG-SKELETON] Creating 1 skeleton block for background`);
                            // Insert single skeleton block for background
                            insertSkeletonBlocks(context, 'background', 1);
                        } else if (context === 'overview' && progressData.total_claims) {
                            console.log(`[DEBUG-SKELETON] Creating 1 skeleton block for overview`);
                            // Insert single skeleton block for overview into summary section
                            insertSkeletonBlocks(context, 'summary', 1);
                        } else if (context === 'detailed-description' && progressData.skeleton_count) {
                            console.log(`[DEBUG-SKELETON] Creating ${progressData.skeleton_count} skeleton blocks for detailed description (limitations + dependent claims)`);
                            // Insert skeleton blocks based on limitations + dependent claims count
                            insertSkeletonBlocks(context, 'detailed', progressData.skeleton_count);
                        } else if (context === 'abstract' && progressData.total_claims) {
                            console.log(`[DEBUG-SKELETON] Creating 1 skeleton block for abstract`);
                            // Insert single skeleton block for abstract
                            insertSkeletonBlocks(context, 'abstract', 1);
                        }
                    }
                    // Handle content updates
                    else if (context === 'summary' && (progressData.type === 'progress' || progressData.type === 'group' || progressData.type === 'group_error')) {
                        // Process summary groups as they arrive
                        const groupKey = `${context}-group-${progressData.claim_numbers?.join('-') || Date.now()}`;

                        if (!processedClaims.has(groupKey)) {
                            // Capture literal support from summary groups for detailed description reuse
                            if (progressData.type === 'group' && progressData.claim_numbers && progressData.content) {
                                console.log('[DEBUG-LITERAL-SUPPORT] Capturing literal support from summary group:', progressData.claim_numbers);
                                
                                if (!summaryLiteralSupport) {
                                    summaryLiteralSupport = {};
                                }
                                
                                // For now, assign the group content to each claim in the group
                                // This is a simplification - ideally we'd have per-claim literal support
                                for (const claimNum of progressData.claim_numbers) {
                                    summaryLiteralSupport[claimNum.toString()] = progressData.content;
                                    console.log('[DEBUG-LITERAL-SUPPORT] Stored literal support for claim', claimNum);
                                }
                            }
                            
                            queueEditorUpdate(context, 'summary', progressData);
                            processedClaims.set(groupKey, true);
                            hasUpdates = true;
                            
                            // Update completed groups count
                            if (progressState.completedGroups.summary !== undefined) {
                                progressState.completedGroups.summary++;
                                progressState.completedSteps++;

                                // Track remaining claims for time estimation
                                const claimsInGroup = progressData.claim_numbers?.length || 0;
                                if (claimsInGroup > 0) {
                                    progressState.summaryRemainingClaims = Math.max(
                                        0,
                                        (progressState.summaryRemainingClaims || 0) - claimsInGroup
                                    );
                                }

                                // Check if all summary groups are complete
                                const summaryComplete = progressState.totalGroups.summary > 0 &&
                                    progressState.completedGroups.summary >= progressState.totalGroups.summary;

                                if (summaryComplete) {
                                    markContextComplete('summary');
                                    insertBoilerplatesAfterContext(specEditor, 'summary', insertedBoilerplates);
                                }

                                // Report progress
                                if (options.onProgress) {
                                    options.onProgress({
                                        type: 'progress',
                                        currentContext: 'summary',
                                        currentTask: `Processing claim group ${progressData.claim_numbers?.join(', ') || ''}`,
                                        totalSteps: progressState.totalSteps,
                                        completedSteps: progressState.completedSteps,
                                        progress: (progressState.completedSteps / progressState.totalSteps) * 100,
                                        estimatedTime: getEstimatedRemainingTime()
                                    });
                                }
                            }
                        }
                    } else if (context === 'embodiments' && progressData.type === 'content') {
                        console.log('[DEBUG-EMBODIMENTS] Processing embodiments content event:', progressData);
                        // Process embodiments content
                        const contextKey = `${context}-content`;

                        if (!processedClaims.has(contextKey)) {
                            console.log('[DEBUG-EMBODIMENTS] Processing new embodiments content');
                            queueEditorUpdate(context, 'embodiments', progressData);
                            processedClaims.set(contextKey, true);
                            hasUpdates = true;
                            progressState.completedSteps++;
                            markContextComplete('embodiments');

                            // Report progress
                            if (options.onProgress) {
                                options.onProgress({
                                    type: 'progress',
                                    currentContext: 'embodiments',
                                    currentTask: 'Generating embodiments',
                                    totalSteps: progressState.totalSteps,
                                    completedSteps: progressState.completedSteps,
                                    progress: (progressState.completedSteps / progressState.totalSteps) * 100,
                                    estimatedTime: getEstimatedRemainingTime()
                                });
                            }

                            // Insert Specification Boilerplate at end of embodiments/examples (in document order)
                            insertBoilerplatesAfterContext(specEditor, 'embodiments', insertedBoilerplates);
                        } else {
                            console.log('[DEBUG-EMBODIMENTS] Embodiments content already processed');
                        }
                    } else if (context === 'background' && progressData.type === 'content') {
                        console.log('[DEBUG-BACKGROUND] Processing background content event:', progressData);
                        // Process background content
                        const contextKey = `${context}-content`;

                        if (!processedClaims.has(contextKey)) {
                            console.log('[DEBUG-BACKGROUND] Processing new background content');
                            queueEditorUpdate(context, 'background', progressData);
                            processedClaims.set(contextKey, true);
                            hasUpdates = true;
                            progressState.completedSteps++;
                            markContextComplete('background');

                            // Report progress
                            if (options.onProgress) {
                                options.onProgress({
                                    type: 'progress',
                                    currentContext: 'background',
                                    currentTask: 'Generating background',
                                    totalSteps: progressState.totalSteps,
                                    completedSteps: progressState.completedSteps,
                                    progress: (progressState.completedSteps / progressState.totalSteps) * 100,
                                    estimatedTime: getEstimatedRemainingTime()
                                });
                            }
                        } else {
                            console.log('[DEBUG-BACKGROUND] Background content already processed');
                        }
                    } else if (context === 'overview' && progressData.type === 'content') {
                        console.log('[DEBUG-OVERVIEW] Processing overview content event:', progressData);
                        // Process overview content
                        const contextKey = `${context}-content`;

                        if (!processedClaims.has(contextKey)) {
                            console.log('[DEBUG-OVERVIEW] Processing new overview content');
                            queueEditorUpdate(context, 'overview', progressData);
                            processedClaims.set(contextKey, true);
                            hasUpdates = true;
                            progressState.completedSteps++;
                            markContextComplete('overview');

                            // Report progress
                            if (options.onProgress) {
                                options.onProgress({
                                    type: 'progress',
                                    currentContext: 'overview',
                                    currentTask: 'Generating overview',
                                    totalSteps: progressState.totalSteps,
                                    completedSteps: progressState.completedSteps,
                                    progress: (progressState.completedSteps / progressState.totalSteps) * 100,
                                    estimatedTime: getEstimatedRemainingTime()
                                });
                            }
                        } else {
                            console.log('[DEBUG-OVERVIEW] Overview content already processed');
                        }
                    } else if (context === 'title' && progressData.type === 'content') {
                        console.log('[DEBUG-TITLE] Processing title content event:', progressData);
                        // Process title content as a regenerative paragraph
                        const contextKey = `${context}-content`;

                        if (!processedClaims.has(contextKey)) {
                            console.log('[DEBUG-TITLE] Processing new title content');
                            queueEditorUpdate(context, 'title', progressData);
                            processedClaims.set(contextKey, true);
                            hasUpdates = true;
                            progressState.completedSteps++;
                            markContextComplete('title');

                            // Report progress
                            if (options.onProgress) {
                                options.onProgress({
                                    type: 'progress',
                                    currentContext: 'title',
                                    currentTask: 'Generating title',
                                    totalSteps: progressState.totalSteps,
                                    completedSteps: progressState.completedSteps,
                                    progress: (progressState.completedSteps / progressState.totalSteps) * 100,
                                    estimatedTime: getEstimatedRemainingTime()
                                });
                            }
                        } else {
                            console.log('[DEBUG-TITLE] Title content already processed');
                        }
                    } else if (context === 'field' && progressData.type === 'content') {
                        console.log('[DEBUG-FIELD] Processing field content event:', progressData);
                        // Process field content as a regenerative paragraph
                        const contextKey = `${context}-content`;

                        if (!processedClaims.has(contextKey)) {
                            console.log('[DEBUG-FIELD] Processing new field content');
                            queueEditorUpdate(context, 'field', progressData);
                            processedClaims.set(contextKey, true);
                            hasUpdates = true;
                            progressState.completedSteps++;
                            markContextComplete('field');

                            // Report progress
                            if (options.onProgress) {
                                options.onProgress({
                                    type: 'progress',
                                    currentContext: 'field',
                                    currentTask: 'Generating field',
                                    totalSteps: progressState.totalSteps,
                                    completedSteps: progressState.completedSteps,
                                    progress: (progressState.completedSteps / progressState.totalSteps) * 100,
                                    estimatedTime: getEstimatedRemainingTime()
                                });
                            }
                        } else {
                            console.log('[DEBUG-FIELD] Field content already processed');
                        }
                    } else if (context === 'abstract' && progressData.type === 'content') {
                        console.log('[DEBUG-ABSTRACT] Processing abstract content event:', progressData);
                        // Process abstract content as a regenerative paragraph
                        const contextKey = `${context}-content`;

                        if (!processedClaims.has(contextKey)) {
                            console.log('[DEBUG-ABSTRACT] Processing new abstract content');
                            queueEditorUpdate(context, 'abstract', progressData);
                            processedClaims.set(contextKey, true);
                            hasUpdates = true;
                            progressState.completedSteps++;
                            markContextComplete('abstract');

                            // Report progress
                            if (options.onProgress) {
                                options.onProgress({
                                    type: 'progress',
                                    currentContext: 'abstract',
                                    currentTask: 'Generating abstract',
                                    totalSteps: progressState.totalSteps,
                                    completedSteps: progressState.completedSteps,
                                    progress: (progressState.completedSteps / progressState.totalSteps) * 100,
                                    estimatedTime: getEstimatedRemainingTime()
                                });
                            }
                        } else {
                            console.log('[DEBUG-ABSTRACT] Abstract content already processed');
                        }
                    } else if (context === 'detailed-description' && progressData.type === 'content') {
                        console.log('[DEBUG-DETAILED-DESCRIPTION] Processing detailed description content event:', progressData);
                        console.log('[DEBUG-DETAILED-DESCRIPTION] Backend content length:', progressData.content?.length);
                        
                        const contextKey = `${context}-content`;
                        if (!processedClaims.has(contextKey)) {
                            
                            // Check if we have literal support from summary to reuse (Path 1 - Fast)
                            if (summaryLiteralSupport && Object.keys(summaryLiteralSupport).length > 0) {
                                console.log('[DEBUG-DETAILED-DESCRIPTION] PATH 1: Using existing literal support from summary');
                                console.log('[DEBUG-DETAILED-DESCRIPTION] Available literal support for claims:', Object.keys(summaryLiteralSupport));
                                
                                // Format literal support content exactly like "Apply to Detailed Description"
                                let formattedContent = '';

                                // Add each claim's literal support as separate paragraphs
                                for (const [_claimNum, literalText] of Object.entries(summaryLiteralSupport)) {
                                    if (literalText && literalText.trim()) {
                                        formattedContent += literalText.trim() + '\n\n';
                                    }
                                }
                                
                                // Create modified progress data with formatted content
                                const modifiedProgressData = {
                                    ...progressData,
                                    content: formattedContent.trim()
                                };
                                
                                console.log('[DEBUG-DETAILED-DESCRIPTION] PATH 1: Using reused literal support (length:', formattedContent.length, ')');
                                
                                // For detailed description, we need to replace ALL skeleton loaders at once
                                // since we get all content in a single event (not streamed per limitation)
                                replaceAllDetailedDescriptionSkeletons(modifiedProgressData.content);
                                
                                // Report progress
                                if (options.onProgress) {
                                    options.onProgress({
                                        type: 'progress',
                                        currentContext: 'detailed-description',
                                        currentTask: 'Applying existing literal support to detailed description',
                                        totalSteps: progressState.totalSteps,
                                        completedSteps: progressState.completedSteps,
                                        progress: (progressState.completedSteps / progressState.totalSteps) * 100,
                                        estimatedTime: getEstimatedRemainingTime()
                                    });
                                }

                            } else {
                                console.log('[DEBUG-DETAILED-DESCRIPTION] PATH 2: No existing literal support, using backend generated content');
                                console.log('[DEBUG-DETAILED-DESCRIPTION] Backend content preview:', progressData.content?.substring(0, 200));
                                
                                // For detailed description, replace ALL skeleton loaders at once
                                replaceAllDetailedDescriptionSkeletons(progressData.content);
                                
                                // Report progress
                                if (options.onProgress) {
                                    options.onProgress({
                                        type: 'progress',
                                        currentContext: 'detailed-description',
                                        currentTask: 'Generating detailed description from claim 1 limitations',
                                        totalSteps: progressState.totalSteps,
                                        completedSteps: progressState.completedSteps,
                                        progress: (progressState.completedSteps / progressState.totalSteps) * 100,
                                        estimatedTime: getEstimatedRemainingTime()
                                    });
                                }
                            }
                            
                            processedClaims.set(contextKey, true);
                            hasUpdates = true;
                            progressState.completedSteps++;
                            markContextComplete('detailed-description');

                            // Check if there were sub-component errors during generation
                            if (progressData.has_errors) {
                                console.warn('[DEBUG-DETAILED-DESCRIPTION] Content generated with some sub-component errors');
                                // Collect sub-component errors for warning display
                                const subcompErrors = [];
                                for (const [key, value] of processedClaims.entries()) {
                                    if (key.startsWith('detailed-description-subcomponent-error-')) {
                                        const subcomp = key.replace('detailed-description-subcomponent-error-', '');
                                        subcompErrors.push(`${subcomp}: ${value}`);
                                    }
                                }

                                if (subcompErrors.length > 0 && options.onProgress) {
                                    options.onProgress({
                                        type: 'warning',
                                        currentContext: 'detailed-description',
                                        message: `Some sub-components had errors: ${subcompErrors.join('; ')}`,
                                        errors: subcompErrors
                                    });
                                }
                            }

                            // Insert Definitions boilerplate at end of detailed section (in document order)
                            insertBoilerplatesAfterContext(specEditor, 'detailed-description', insertedBoilerplates);

                        } else {
                            console.log('[DEBUG-DETAILED-DESCRIPTION] Detailed description content already processed');
                        }
                    }
                    // Handle sub-component progress events (granular progress for detailed-description)
                    else if (progressData.type === 'subcomponent-progress') {
                        console.log(`[SPEC-REGEN] Sub-component progress: ${progressData.subcomponent} - ${progressData.status}`);

                        // Report granular progress to UI
                        if (options.onProgress) {
                            const subcompNames = {
                                'limitations': 'Extracting limitations from claim 1',
                                'opening-paragraph': 'Generating opening paragraph',
                                'problem-introduction': 'Generating problem introduction',
                                'solution-introduction': 'Generating solution introduction',
                                'advantages': 'Generating advantages',
                                'subheading': 'Generating subheading',
                                'sanitized-idf': 'Processing invention disclosure',
                                'idf-subheading': 'Generating IDF subheading',
                                'literal-support': 'Generating literal support'
                            };

                            options.onProgress({
                                type: 'progress',
                                currentContext: 'detailed-description',
                                currentTask: subcompNames[progressData.subcomponent] || progressData.subcomponent,
                                subcomponent: progressData.subcomponent,
                                subcomponentStatus: progressData.status,
                                totalSteps: progressState.totalSteps,
                                completedSteps: progressState.completedSteps,
                                progress: (progressState.completedSteps / progressState.totalSteps) * 100,
                                estimatedTime: getEstimatedRemainingTime()
                            });
                        }
                    }
                    // Handle sub-component error events (granular errors for detailed-description)
                    else if (progressData.type === 'subcomponent-error') {
                        console.error(`[SPEC-REGEN] Sub-component error: ${progressData.subcomponent} - ${progressData.message}`);

                        // Track sub-component errors for display
                        const errorKey = `${context}-subcomponent-error-${progressData.subcomponent}`;
                        if (!processedClaims.has(errorKey)) {
                            processedClaims.set(errorKey, progressData.message);

                            // Report granular error to UI
                            if (options.onProgress) {
                                const subcompNames = {
                                    'limitations': 'Limitations extraction',
                                    'opening-paragraph': 'Opening paragraph',
                                    'problem-introduction': 'Problem introduction',
                                    'solution-introduction': 'Solution introduction',
                                    'advantages': 'Advantages',
                                    'subheading': 'Subheading',
                                    'sanitized-idf': 'IDF processing',
                                    'idf-subheading': 'IDF subheading',
                                    'literal-support': 'Literal support'
                                };

                                options.onProgress({
                                    type: 'subcomponent-error',
                                    currentContext: 'detailed-description',
                                    subcomponent: progressData.subcomponent,
                                    subcomponentName: subcompNames[progressData.subcomponent] || progressData.subcomponent,
                                    errorMessage: progressData.message
                                });
                            }
                        }
                    }
                    // Handle error events for all contexts
                    else if (progressData.type === 'error') {
                        console.error(`[SPEC-REGEN] Error event received for context ${context}:`, progressData.message);

                        // Special handling for detailed-description with sub-component errors
                        if (context === 'detailed-description') {
                            // Collect all sub-component errors with their friendly names
                            const subcompNames = {
                                'limitations': 'Limitations extraction',
                                'opening-paragraph': 'Opening paragraph',
                                'problem-introduction': 'Problem introduction',
                                'solution-introduction': 'Solution introduction',
                                'advantages': 'Advantages',
                                'subheading': 'Subheading',
                                'sanitized-idf': 'IDF processing',
                                'idf-subheading': 'IDF subheading',
                                'literal-support': 'Literal support'
                            };

                            const subcompErrors = [];
                            for (const [key, value] of processedClaims.entries()) {
                                if (key.startsWith('detailed-description-subcomponent-error-')) {
                                    const subcomp = key.replace('detailed-description-subcomponent-error-', '');
                                    const friendlyName = subcompNames[subcomp] || subcomp;
                                    subcompErrors.push({
                                        subcomponent: subcomp,
                                        name: friendlyName,
                                        message: value
                                    });
                                }
                            }

                            if (subcompErrors.length > 0) {
                                // Insert individual error paragraphs for each sub-component
                                insertMultipleSubcomponentErrors(specEditor, context, subcompErrors);
                            } else {
                                // No sub-component errors tracked, show generic error
                                const errorType = extractErrorType(progressData.message);
                                const errorMessage = `[[Error: unable to generate detailed description due to ${errorType}. Please try again.]]`;
                                insertErrorIntoRegenerativeBlock(specEditor, context, errorMessage);
                            }
                        } else {
                            // Standard error handling for other contexts
                            const contextNames = {
                                'background': 'background',
                                'overview': 'overview',
                                'summary': 'brief summary',
                                'embodiments': 'embodiments',
                                'title': 'title',
                                'field': 'field of invention',
                                'abstract': 'abstract'
                            };
                            const contextName = contextNames[context] || context;
                            const errorType = extractErrorType(progressData.message);
                            const errorMessage = `[[Error: unable to generate ${contextName} due to ${errorType}. Please try again.]]`;

                            // Special handling for title - update the heading text directly
                            if (context === 'title') {
                                updateTitleHeading(errorMessage);
                            } else {
                                insertErrorIntoRegenerativeBlock(specEditor, context, errorMessage);
                            }
                        }

                        // Mark as processed so we don't keep trying
                        const contextKey = `${context}-content`;
                        processedClaims.set(contextKey, true);
                        progressState.completedSteps++;
                    }
                }
            }
        );

        // Helper function to update the title heading
        function updateTitleHeading(titleText) {
            console.log('[DEBUG-TITLE] Updating title heading with:', titleText);

            const doc = specEditor.state.doc;
            let tr = specEditor.state.tr;
            let titleFound = false;

            // Debug: log all heading nodes
            console.log('[DEBUG-TITLE] Searching for title heading...');
            doc.descendants((node, pos) => {
                if (node.type.name === 'heading') {
                    console.log(`[DEBUG-TITLE] Found heading at pos ${pos}:`, {
                        level: node.attrs.level,
                        blockType: node.attrs.blockType,
                        sectionType: node.attrs.sectionType,
                        text: node.textContent
                    });
                }
            });

            // Find the existing title heading (level 2 with sectionType: 'title')
            doc.descendants((node, pos) => {
                if (node.type.name === 'heading' &&
                    node.attrs.level === 2 &&
                    node.attrs.sectionType === 'title') {

                    console.log('[DEBUG-TITLE] Found existing title at pos', pos, 'with text:', node.textContent);

                    // Replace the title text
                    const newTitleNode = specEditor.schema.nodes.heading.create(
                        {
                            level: 2,
                            sectionType: 'title',
                            class: 'section-title'
                        },
                        specEditor.schema.text(titleText)
                    );

                    tr = tr.replaceWith(pos, pos + node.nodeSize, newTitleNode);
                    titleFound = true;
                    return false; // Stop searching
                }
            });

            if (!titleFound) {
                console.log('[DEBUG-TITLE] No existing title found, creating new title at the beginning');
                // If no title exists, insert at the beginning
                const newTitleNode = specEditor.schema.nodes.heading.create(
                    {
                        level: 2,
                        sectionType: 'title',
                        class: 'section-title'
                    },
                    specEditor.schema.text(titleText)
                );

                tr = tr.insert(0, newTitleNode);
            }

            if (tr.docChanged) {
                specEditor.view.dispatch(tr);
                console.log('[DEBUG-TITLE] Title updated successfully to:', titleText);
            } else {
                console.log('[DEBUG-TITLE] No changes made to title');
            }
        }

        // Helper function to insert skeleton loader blocks
        function insertSkeletonBlocks(context, sectionType, count) {
            console.log(`[DEBUG-SKELETON] Inserting ${count} skeleton blocks for ${context}`);

            // First, clear any existing regenerative blocks for this context
            const doc = specEditor.state.doc;
            let tr = specEditor.state.tr;

            // Collect blocks to delete for this specific context
            const blocksToDelete = [];
            doc.descendants((node, pos) => {
                // Delete paragraph blocks with matching context
                if (node.type.name === 'paragraph' &&
                    (node.attrs.blockType === 'regenerative-support' || node.attrs.blockType === 'skeleton-loader') &&
                    node.attrs.context === context) {
                    blocksToDelete.push({ pos, size: node.nodeSize });
                }
                // Also delete subheading and IDF heading nodes for detailed-description context
                if (context === 'detailed-description' &&
                    node.type.name === 'heading' &&
                    (node.attrs.sectionType === 'subheading' || node.attrs.sectionType === 'idf-heading')) {
                    blocksToDelete.push({ pos, size: node.nodeSize });
                }
            });
            
            // Delete in reverse order
            blocksToDelete.reverse();
            for (const block of blocksToDelete) {
                tr = tr.delete(block.pos, block.pos + block.size);
            }
            
            // Find insertion position (after the section header)
            let insertPos = null;
            tr.doc.descendants((node, pos) => {
                if (node.type.name === 'heading' && 
                    node.attrs.sectionType === sectionType) {
                    insertPos = pos + node.nodeSize;
                    return false;
                }
            });
            
            // For summary context, insert after any existing overview blocks
            if (context === 'summary' && insertPos !== null) {
                let overviewBlocksFound = 0;
                tr.doc.descendants((node, pos) => {
                    if (pos >= insertPos && 
                        node.type.name === 'paragraph' && 
                        node.attrs.context === 'overview') {
                        overviewBlocksFound++;
                        insertPos = pos + node.nodeSize;
                    }
                });
                console.log(`[DEBUG-SKELETON] Found ${overviewBlocksFound} overview blocks, adjusted insertPos to ${insertPos}`);
            }
            
            if (insertPos !== null) {
                // Insert skeleton blocks
                let currentPos = insertPos;
                
                for (let i = 0; i < count; i++) {
                    // Create skeleton content as an empty paragraph with skeleton styling
                    // The CSS will handle the visual appearance
                    const skeletonContent = specEditor.schema.nodes.paragraph.create(
                        {
                            blockType: 'skeleton-loader',
                            context: context,
                            skeletonIndex: i  // Track which skeleton this is for replacement
                        },
                        // Add a non-breaking space to ensure the paragraph has content
                        specEditor.schema.text('\u00A0')
                    );
                    
                    tr = tr.insert(currentPos, skeletonContent);
                    currentPos += skeletonContent.nodeSize;
                }
                
                specEditor.view.dispatch(tr);
                console.log(`[DEBUG-SKELETON] Inserted ${count} skeleton blocks at position ${insertPos}`);
            }
        }

        // Helper function to parse inline markdown (bold, italic, figure/numeral references) and create text nodes with marks
        function parseInlineMarkdown(text) {
            const textNodes = [];
            // Match **bold**, *italic*, or @{figure:...}/@{numeral:...} patterns
            // Group 1: full bold match (**text**)
            // Group 2: bold content
            // Group 3: full italic match (*text*)
            // Group 4: italic content
            // Group 5: full figure/numeral reference (@{type:content})
            // Group 6: reference type (figure or numeral)
            // Group 7: reference content (name or name_value)
            const regex = /(\*\*(.+?)\*\*)|(\*(.+?)\*)|(@\{(figure|numeral):([^}]+)\})/g;
            let lastIndex = 0;
            let match;

            while ((match = regex.exec(text)) !== null) {
                // Add text before the match
                if (match.index > lastIndex) {
                    const beforeText = text.slice(lastIndex, match.index);
                    if (beforeText) {
                        textNodes.push(specEditor.schema.text(beforeText));
                    }
                }

                if (match[2]) {
                    // Bold text (**text**)
                    const boldMark = specEditor.schema.marks.bold;
                    if (boldMark) {
                        textNodes.push(specEditor.schema.text(match[2], [boldMark.create()]));
                    } else {
                        textNodes.push(specEditor.schema.text(match[2]));
                    }
                } else if (match[4]) {
                    // Italic text (*text*)
                    const italicMark = specEditor.schema.marks.italic;
                    if (italicMark) {
                        textNodes.push(specEditor.schema.text(match[4], [italicMark.create()]));
                    } else {
                        textNodes.push(specEditor.schema.text(match[4]));
                    }
                } else if (match[5]) {
                    // Figure or numeral reference (@{figure:...} or @{numeral:...})
                    const fullMatch = match[5]; // e.g., "@{figure:ar-device}"
                    const refType = match[6];   // "figure" or "numeral"
                    const refContent = match[7]; // e.g., "ar-device" or "head_102"

                    // Create a placeholder figureKey - works even if figure doesn't exist
                    const figureKey = `generated:${refType}:${refContent}`;

                    const figureRefMark = specEditor.schema.marks.figureReference;
                    if (figureRefMark) {
                        textNodes.push(specEditor.schema.text(fullMatch, [figureRefMark.create({ figureKey })]));
                    } else {
                        // Fallback if figureReference mark doesn't exist
                        textNodes.push(specEditor.schema.text(fullMatch));
                    }
                }

                lastIndex = regex.lastIndex;
            }

            // Add remaining text after last match
            if (lastIndex < text.length) {
                const afterText = text.slice(lastIndex);
                if (afterText) {
                    textNodes.push(specEditor.schema.text(afterText));
                }
            }

            // If no matches found, return the original text
            if (textNodes.length === 0 && text) {
                textNodes.push(specEditor.schema.text(text));
            }

            return textNodes;
        }

        // Helper function to create a paragraph with inline markdown parsing
        function createParagraphWithMarkdown(text, attrs = {}) {
            const defaultAttrs = {
                blockType: 'regenerative-support',
                context: 'detailed-description',
                ...attrs
            };
            const textNodes = parseInlineMarkdown(text);
            if (textNodes.length === 0) {
                return specEditor.schema.nodes.paragraph.create(defaultAttrs);
            }
            return specEditor.schema.nodes.paragraph.create(defaultAttrs, textNodes);
        }

        // Helper function to parse a markdown block and create appropriate ProseMirror node(s)
        // For IDF content: # becomes level 4, ## becomes level 5, etc. (shifted by 3)
        // For subheadings (## prefix from title generation): stays at level 3
        function parseMarkdownBlock(block, isSubheading = false) {
            // Don't trim the block - preserve leading whitespace for indentation detection
            const trimmedForHeadingCheck = block.trim();
            const nodes = [];

            // Check for markdown heading (# syntax)
            const headingMatch = trimmedForHeadingCheck.match(/^(#{1,6})\s+(.+)$/);
            console.log('[DEBUG-PARSE-BLOCK] Checking for heading in block:', JSON.stringify(trimmedForHeadingCheck.substring(0, 100)));
            console.log('[DEBUG-PARSE-BLOCK] Heading match result:', headingMatch);
            if (headingMatch) {
                const hashes = headingMatch[1].length;
                const headingText = headingMatch[2].trim();
                console.log('[DEBUG-PARSE-BLOCK] Creating heading - hashes:', hashes, 'text:', headingText, 'isSubheading:', isSubheading);

                // For subheadings (## from title generation), use level 3
                // For IDF markdown headings, shift by 3 (# -> 4, ## -> 5, etc.)
                let level;
                if (isSubheading && hashes === 2) {
                    level = 3;
                } else {
                    // IDF headings: # -> level 4 (subsubsection), ## -> level 5, etc.
                    level = hashes + 3;
                    // Cap at level 6 (HTML max)
                    if (level > 6) level = 6;
                }

                const attrs = {
                    level: level,
                    class: level === 3 ? 'section-subheading' : 'idf-heading',
                    sectionType: level === 3 ? 'subheading' : 'idf-heading'
                    // Note: No blockType for subheadings - they should render as normal headings
                };

                // Parse inline markdown in heading text
                const textNodes = parseInlineMarkdown(headingText);
                const headingNode = specEditor.schema.nodes.heading.create(attrs, textNodes);
                console.log('[DEBUG-PARSE-BLOCK] Created heading node:', headingNode.type.name, 'level:', headingNode.attrs.level, 'blockType:', headingNode.attrs.blockType);
                nodes.push(headingNode);
                return nodes;
            }

            // Check for bullet list items (- or * with any amount of whitespace)
            // Also track indentation level for nested lists
            // DON'T trim lines yet - preserve leading whitespace for indent detection
            const lines = block.split('\n');
            const listItems = []; // Array of { indent, type, content }
            let regularLines = [];

            for (const line of lines) {
                // Match bullet items with optional leading whitespace (indentation)
                // Captures: [1] = leading spaces, [2] = bullet char, [3] = content
                const bulletMatch = line.match(/^(\s*)([-*])\s+(.+)$/);
                // Match numbered items with optional leading whitespace
                // Captures: [1] = leading spaces, [2] = the number, [3] = content
                const numberedMatch = line.match(/^(\s*)(\d+)\.\s+(.+)$/);

                if (bulletMatch) {
                    // Flush any regular lines first
                    if (regularLines.length > 0) {
                        nodes.push(createParagraphWithMarkdown(regularLines.join(' ').trim()));
                        regularLines = [];
                    }
                    const indent = bulletMatch[1].length;
                    listItems.push({ indent, type: 'bullet', content: bulletMatch[3].trim() });
                } else if (numberedMatch) {
                    // Flush any regular lines first
                    if (regularLines.length > 0) {
                        nodes.push(createParagraphWithMarkdown(regularLines.join(' ').trim()));
                        regularLines = [];
                    }
                    const indent = numberedMatch[1].length;
                    const number = parseInt(numberedMatch[2], 10); // Capture the original number
                    listItems.push({ indent, type: 'numbered', number, content: numberedMatch[3].trim() });
                } else if (line.trim()) {
                    // Flush any list items first
                    if (listItems.length > 0) {
                        const listNodes = createListNodesWithIndent(listItems);
                        nodes.push(...listNodes);
                        listItems.length = 0;
                    }
                    regularLines.push(line.trim());
                }
            }

            // Flush remaining items
            if (listItems.length > 0) {
                const listNodes = createListNodesWithIndent(listItems);
                nodes.push(...listNodes);
            }
            if (regularLines.length > 0) {
                nodes.push(createParagraphWithMarkdown(regularLines.join(' ').trim()));
            }

            // Helper to create list nodes with proper nesting based on indentation
            // Since ProseMirror nested lists are complex and we're using fallback paragraphs,
            // we'll create paragraphs with proper indentation styling
            function createListNodesWithIndent(items) {
                const result = [];

                if (items.length === 0) return result;

                // Detect list structure: bullets after numbered items are sub-items
                // First pass: identify if we have a mixed numbered/bullet list
                let hasNumberedItems = items.some(i => i.type === 'numbered');
                let hasBulletItems = items.some(i => i.type === 'bullet');
                let isHierarchicalList = hasNumberedItems && hasBulletItems;

                // Track the last numbered item to know when bullets should be indented
                let afterNumberedItem = false;

                // Track numbered list counter
                let numberedCounter = 1;

                for (const item of items) {
                    // Calculate indent level
                    // For hierarchical lists (numbered + bullets), bullets after numbered items get 1 level indent
                    let indentLevel = 0;

                    if (isHierarchicalList) {
                        if (item.type === 'numbered') {
                            afterNumberedItem = true;
                            indentLevel = 0; // Numbered items at base level
                        } else if (item.type === 'bullet' && afterNumberedItem) {
                            indentLevel = 1; // Bullets after numbered items are indented
                        }
                    } else {
                        // Non-hierarchical: use explicit whitespace indentation if present
                        // Each 2 spaces = 1 indent level
                        const minIndent = Math.min(...items.map(i => i.indent));
                        indentLevel = Math.floor((item.indent - minIndent) / 2);
                    }

                    // Create paragraph with bullet/number prefix
                    // Use non-breaking spaces for indentation to prevent collapse
                    const indentSpaces = '\u00A0\u00A0\u00A0\u00A0'.repeat(indentLevel); // 4 non-breaking spaces per level

                    let prefix;
                    if (item.type === 'bullet') {
                        prefix = `${indentSpaces}• `;
                    } else {
                        // Numbered item - use the original number if available, otherwise increment
                        prefix = `${indentSpaces}${item.number || numberedCounter}. `;
                        numberedCounter++;
                    }

                    // Parse inline markdown and prepend the prefix
                    const textNodes = parseInlineMarkdown(item.content);
                    const prefixNode = specEditor.schema.text(prefix);

                    const paragraph = specEditor.schema.nodes.paragraph.create(
                        {
                            blockType: 'regenerative-support',
                            context: 'detailed-description'
                        },
                        [prefixNode, ...textNodes]
                    );

                    result.push(paragraph);
                }

                return result;
            }

            // If no nodes were created, create a simple paragraph
            if (nodes.length === 0 && trimmedForHeadingCheck) {
                nodes.push(createParagraphWithMarkdown(trimmedForHeadingCheck));
            }

            return nodes;
        }

        // Helper function to replace all detailed description skeleton loaders at once
        function replaceAllDetailedDescriptionSkeletons(content) {
            console.log('[DEBUG-SKELETON] Replacing all detailed description skeleton loaders with content');

            const doc = specEditor.state.doc;
            let tr = specEditor.state.tr;

            // Find all skeleton loaders for detailed-description
            const skeletonsToReplace = [];
            doc.descendants((node, pos) => {
                if (node.type.name === 'paragraph' &&
                    node.attrs.blockType === 'skeleton-loader' &&
                    node.attrs.context === 'detailed-description') {
                    skeletonsToReplace.push({ pos, size: node.nodeSize });
                }
            });

            console.log(`[DEBUG-SKELETON] Found ${skeletonsToReplace.length} detailed description skeleton loaders to replace`);

            // Helper to split content into logical blocks while preserving list structure
            function splitIntoLogicalBlocks(content) {
                // Preprocess: if [[IDF]] is followed by a markdown header on the same line,
                // split them onto separate lines so the header can be properly parsed
                content = content.replace(/^\[\[IDF\]\]\s*(#{1,6}\s+)/m, '[[IDF]]\n$1');

                const lines = content.split('\n');
                const blocks = [];
                let currentBlock = [];
                let inList = false;

                for (const line of lines) {
                    const trimmedLine = line.trim();
                    const isListItem = /^(\s*)([-*])\s+/.test(line) || /^(\s*)(\d+)\.\s+/.test(line);
                    const isHeading = /^#{1,6}\s+/.test(trimmedLine);
                    const isEmpty = trimmedLine === '';

                    if (isHeading) {
                        // Flush current block
                        if (currentBlock.length > 0) {
                            blocks.push(currentBlock.join('\n'));
                            currentBlock = [];
                        }
                        // Heading is its own block
                        blocks.push(line);
                        inList = false;
                    } else if (isListItem) {
                        // If we weren't in a list and have content, flush it
                        if (!inList && currentBlock.length > 0) {
                            blocks.push(currentBlock.join('\n'));
                            currentBlock = [];
                        }
                        currentBlock.push(line);
                        inList = true;
                    } else if (isEmpty) {
                        // Empty line - if we're in a list, keep going (lists can have blank lines)
                        // If not in a list, flush current block
                        if (!inList && currentBlock.length > 0) {
                            blocks.push(currentBlock.join('\n'));
                            currentBlock = [];
                        }
                        // Don't add empty lines to list blocks
                    } else {
                        // Regular text
                        if (inList) {
                            // End the list, flush it
                            if (currentBlock.length > 0) {
                                blocks.push(currentBlock.join('\n'));
                                currentBlock = [];
                            }
                            inList = false;
                        }
                        currentBlock.push(line);
                    }
                }

                // Flush any remaining content
                if (currentBlock.length > 0) {
                    blocks.push(currentBlock.join('\n'));
                }

                // Filter out empty blocks only
                return blocks.filter(b => b.trim());
            }

            if (skeletonsToReplace.length === 0) {
                console.log('[DEBUG-SKELETON] No skeleton loaders found, inserting content directly');
                // Find the detailed section to insert content
                let insertPos = null;
                doc.descendants((node, pos) => {
                    if (node.type.name === 'heading' &&
                        node.attrs.sectionType === 'detailed') {
                        insertPos = pos + node.nodeSize;
                        return false;
                    }
                });

                if (insertPos !== null) {
                    // Split content into logical blocks (keeps list items together)
                    const blocks = splitIntoLogicalBlocks(content);
                    console.log('[DEBUG-BLOCKS] Logical blocks:', blocks.length, blocks.map(b => b.substring(0, 50) + '...'));
                    for (const block of blocks) {
                        // Check if this is a subheading (## prefix from title generation)
                        const isSubheading = block.trim().startsWith('## ');
                        const parsedNodes = parseMarkdownBlock(block, isSubheading);
                        for (const node of parsedNodes) {
                            tr = tr.insert(insertPos, node);
                            insertPos += node.nodeSize;
                        }
                    }
                    specEditor.view.dispatch(tr);
                }
                return;
            }

            // Replace all skeleton loaders in reverse order to maintain positions
            // First, delete all skeleton loaders
            for (let i = skeletonsToReplace.length - 1; i >= 0; i--) {
                const skeleton = skeletonsToReplace[i];
                tr = tr.delete(skeleton.pos, skeleton.pos + skeleton.size);
            }

            // Then insert the content at the first skeleton's position
            const insertPos = skeletonsToReplace[0].pos;
            let currentPos = insertPos;

            // Split content into logical blocks (keeps list items together)
            const blocks = splitIntoLogicalBlocks(content);
            console.log('[DEBUG-BLOCKS] Logical blocks:', blocks.length, blocks.map(b => b.substring(0, 50) + '...'));

            for (const block of blocks) {
                // Check if this is a subheading (## prefix from title generation)
                const isSubheading = block.trim().startsWith('## ');
                const parsedNodes = parseMarkdownBlock(block, isSubheading);
                for (const node of parsedNodes) {
                    tr = tr.insert(currentPos, node);
                    currentPos += node.nodeSize;
                }
            }

            specEditor.view.dispatch(tr);
            console.log(`[DEBUG-SKELETON] Replaced ${skeletonsToReplace.length} skeleton loaders with parsed markdown content`);
        }

        // Helper function to immediately process editor updates
        function queueEditorUpdate(context, sectionType, progressData) {
            // No more queuing - just process immediately
            performEditorUpdate(context, sectionType, progressData);
        }

        // Helper function to perform the actual editor update
        function performEditorUpdate(context, sectionType, progressData) {
            // Special handling for title - update the heading directly
            if (context === 'title') {
                const titleText = progressData.content || '';
                if (titleText) {
                    updateTitleHeading(titleText);
                }
                return;
            }

            // NodeViews handle cleanup automatically when nodes are replaced

            // Track which skeleton to replace for this context
            const skeletonKey = `${context}-next-skeleton`;
            if (!processedClaims.has(skeletonKey)) {
                processedClaims.set(skeletonKey, 0); // Initialize skeleton counter
            }
            const skeletonIndex = processedClaims.get(skeletonKey);

            console.log(`[DEBUG-SKELETON] Replacing skeleton ${skeletonIndex} for ${context}`);
            
            // Find and replace the next skeleton block
            const doc = specEditor.state.doc;
            let tr = specEditor.state.tr;
            let skeletonFound = false;
            let skeletonPos = null;
            let skeletonSize = null;
            
            // Find the skeleton block to replace
            doc.descendants((node, pos) => {
                if (!skeletonFound && 
                    node.type.name === 'paragraph' && 
                    node.attrs.blockType === 'skeleton-loader' && 
                    node.attrs.context === context &&
                    node.attrs.skeletonIndex === skeletonIndex) {
                    skeletonPos = pos;
                    skeletonSize = node.nodeSize;
                    skeletonFound = true;
                    return false;
                }
            });
            
            if (!skeletonFound) {
                console.log(`[DEBUG-SKELETON] No skeleton found at index ${skeletonIndex} for ${context}, falling back to append`);
                // Debug: let's see all available skeleton blocks
                doc.descendants((node, _pos) => {
                    if (node.type.name === 'paragraph' && node.attrs.blockType === 'skeleton-loader') {
                        console.log(`[DEBUG-SKELETON] Available skeleton: context=${node.attrs.context}, index=${node.attrs.skeletonIndex}`);
                    }
                });
                // Fallback: find insertion position after section header or last content block
                doc.descendants((node, pos) => {
                    if (node.type.name === 'heading' && 
                        node.attrs.sectionType === sectionType) {
                        skeletonPos = pos + node.nodeSize;
                        skeletonSize = 0; // We're inserting, not replacing
                    } else if (node.type.name === 'paragraph' && 
                              node.attrs.blockType === 'regenerative-support' && 
                              node.attrs.context === context) {
                        // Update position to after the last regenerative block
                        skeletonPos = pos + node.nodeSize;
                        skeletonSize = 0;
                    }
                });
            } else {
                console.log(`[DEBUG-SKELETON] Found skeleton at index ${skeletonIndex} for ${context} at pos ${skeletonPos}`);
            }
            
            // Update skeleton counter for next replacement
            processedClaims.set(skeletonKey, skeletonIndex + 1);
            
            // Process new content
            const content = progressData.content || progressData.message || '';
            const paragraphs = content.split('\n\n').filter(p => p.trim());
            const finalParagraphs = paragraphs.length > 0 ? paragraphs : [content];
            
            if (context === 'embodiments' && finalParagraphs.length > 1) {
                // Special handling for embodiments: replace multiple skeletons with split content
                console.log(`[DEBUG-SKELETON] Embodiments has ${finalParagraphs.length} paragraphs, replacing multiple skeletons`);
                
                const doc = specEditor.state.doc;
                let tr = specEditor.state.tr;
                
                // Find all remaining skeleton blocks for embodiments
                const skeletonBlocks = [];
                doc.descendants((node, pos) => {
                    if (node.type.name === 'paragraph' && 
                        node.attrs.blockType === 'skeleton-loader' && 
                        node.attrs.context === 'embodiments') {
                        console.log(`[DEBUG-SKELETON] Found skeleton node to replace:`, node.attrs);
                        skeletonBlocks.push({ pos, size: node.nodeSize, index: node.attrs.skeletonIndex });
                    }
                });
                console.log(`[DEBUG-SKELETON] Found ${skeletonBlocks.length} skeleton blocks for embodiments`);
                
                // Sort by skeleton index
                skeletonBlocks.sort((a, b) => a.index - b.index);
                
                // Replace skeletons with paragraphs (in reverse order to maintain positions)
                for (let i = Math.min(skeletonBlocks.length, finalParagraphs.length) - 1; i >= 0; i--) {
                    const skeleton = skeletonBlocks[i];
                    const paragraph = finalParagraphs[i];
                    
                    // For NodeView replacement, we need to replace the entire node
                    const attrs = {
                        blockType: 'regenerative-support',
                        context: context
                    };
                    
                    const paragraphNode = specEditor.schema.nodes.paragraph.create(
                        attrs,
                        specEditor.schema.text(paragraph.trim())
                    );
                    console.log(`[DEBUG-SKELETON] Created paragraph node:`, paragraphNode);
                    console.log(`[DEBUG-SKELETON] Paragraph text:`, paragraph.trim());
                    
                    // Replace the skeleton node entirely
                    tr = tr.replaceWith(skeleton.pos, skeleton.pos + skeleton.size, paragraphNode);
                }
                
                specEditor.view.dispatch(tr);
                console.log(`[DEBUG-SKELETON] Replaced ${Math.min(skeletonBlocks.length, finalParagraphs.length)} embodiments skeletons with content`);
                
            } else {
                // Normal single skeleton replacement for summary or single paragraph content
                if (skeletonPos !== null && skeletonSize > 0) {
                    // For single paragraph replacement, use replaceWith
                    if (finalParagraphs.length === 1) {
                        const attrs = {
                            blockType: 'regenerative-support',
                            context: context
                        };
                        
                        if (context === 'summary' && progressData.claim_numbers) {
                            attrs.claimGroup = progressData.claim_numbers.join(',');
                        }
                        
                        const paragraphNode = specEditor.schema.nodes.paragraph.create(
                            attrs,
                            specEditor.schema.text(finalParagraphs[0].trim())
                        );
                        
                        tr = tr.replaceWith(skeletonPos, skeletonPos + skeletonSize, paragraphNode);
                    } else {
                        // Multiple paragraphs - delete and insert
                        tr = tr.delete(skeletonPos, skeletonPos + skeletonSize);
                        
                        let currentPos = skeletonPos;
                        for (const paragraph of finalParagraphs) {
                            const attrs = {
                                blockType: 'regenerative-support',
                                context: context
                            };
                            
                            if (context === 'summary' && progressData.claim_numbers) {
                                attrs.claimGroup = progressData.claim_numbers.join(',');
                            }
                            
                            const paragraphNode = specEditor.schema.nodes.paragraph.create(
                                attrs,
                                specEditor.schema.text(paragraph.trim())
                            );
                            
                            tr = tr.insert(currentPos, paragraphNode);
                            currentPos += paragraphNode.nodeSize;
                        }
                    }
                    
                    specEditor.view.dispatch(tr);
                    console.log(`[DEBUG-SKELETON] Replaced skeleton ${skeletonIndex} with ${finalParagraphs.length} paragraphs for ${context}`);
                }
            }
        }

        console.log('DocumentRegenerationService: Got result from backend:', result);
        console.log('DocumentRegenerationService: Final result.results:', result?.results);
        console.timeEnd('Backend service call');

        // If result is null (aborted), exit early without calling completion callback
        if (!result) {
            console.log('DocumentRegenerationService: Request was aborted, exiting early');
            return false;
        }

        // Process any groups in final result that weren't streamed
        if (result && result.results && result.results.summary) {
            const summaryGroups = result.results.summary;
            console.log('DocumentRegenerationService: Found', summaryGroups.length, 'summary groups in final result');
            console.log('DocumentRegenerationService: Processed claims so far:', Array.from(processedClaims.keys()));
            
            // Capture literal support from summary for reuse in detailed description
            if (result.literal_support) {
                console.log('DocumentRegenerationService: Capturing literal support from summary for detailed description reuse');
                summaryLiteralSupport = result.literal_support;
            }
            
            for (let i = 0; i < summaryGroups.length; i++) {
                const group = summaryGroups[i];
                const groupKey = `summary-group-${group.claim_numbers?.join('-') || Date.now()}`;
                
                console.log(`Group ${i}: claims=${group.claim_numbers}, key=${groupKey}, processed=${processedClaims.has(groupKey)}`);
                
                if (!processedClaims.has(groupKey)) {
                    console.log('DocumentRegenerationService: Adding missing group:', group.content.substring(0, 50));
                    // This group wasn't processed during streaming, add it now
                    const groupData = {
                        type: 'progress',
                        context: 'summary',
                        claim_numbers: group.claim_numbers,
                        content: group.content
                    };
                    performEditorUpdate('summary', 'summary', groupData);
                    hasUpdates = true;
                } else {
                    console.log('DocumentRegenerationService: Group already processed during streaming');
                }
            }

            // Insert Incorporation by Reference and Drawings boilerplates after summary (in document order)
            insertBoilerplatesAfterContext(specEditor, 'summary', insertedBoilerplates);
        }

        // Process embodiments in final result if not streamed
        if (result && result.results && result.results.embodiments) {
            const embodimentsData = result.results.embodiments[0]; // Embodiments is typically a single entry
            const contextKey = 'embodiments-content';
            
            console.log('DocumentRegenerationService: Found embodiments in final result');
            console.log('DocumentRegenerationService: Embodiments processed?', processedClaims.has(contextKey));
            
            if (!processedClaims.has(contextKey) && embodimentsData && embodimentsData.content) {
                console.log('DocumentRegenerationService: Adding missing embodiments content:', embodimentsData.content.substring(0, 50));
                const embodimentsEventData = {
                    type: 'content',
                    context: 'embodiments',
                    content: embodimentsData.content
                };
                performEditorUpdate('embodiments', 'embodiments', embodimentsEventData);
                hasUpdates = true;

                // Insert Specification Boilerplate at end of embodiments/examples (in document order)
                insertBoilerplatesAfterContext(specEditor, 'embodiments', insertedBoilerplates);
            } else {
                console.log('DocumentRegenerationService: Embodiments already processed during streaming or no content found');
            }
        }

        // No pending transactions to clean up - everything processed immediately
        
        // Streaming is complete - no fallback processing needed
        console.log('DocumentRegenerationService: Streaming updates complete, hasUpdates:', hasUpdates);
        
        // Report completion
        if (options.onProgress) {
            options.onProgress({
                type: 'complete',
                totalSteps: progressState.totalSteps,
                completedSteps: progressState.totalSteps,
                progress: 100
            });
        }

        return true;
        
    } catch (error) {
        console.error('DocumentRegenerationService: Error regenerating spec:', error);
        return false;
    } finally {
        console.log('DocumentRegenerationService: Cleaning up');
        console.timeEnd('Total regeneration time');
        
        // Small delay before turning off the spinner to ensure DOM updates are complete
        setTimeout(() => {
            // Turn off spinner animation by setting isRegenerating to false
            specStore.update(s => ({ ...s, isRegenerating: false }));
            editorStateStore.update(s => ({ ...s, isAnalyzing: false }));
        }, 100);
    }
}